import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Print } from '@mui/icons-material';
import { useRouter } from '../contexts/RouterContext';
import { useAuth } from '../contexts/AuthContext';
import { patientsService, PatientDetail } from '../services/patientsService';
import { printTemplateService, renderTemplateHtml, PrintTemplate } from '../services/printTemplateService';
import { PrintTemplateBar, usePrintTemplateChoice } from './print/PrintTemplateChoice';
import { FinancialSession } from '../services/patientFinancialService';
import { colors } from '../theme/designSystem';

/**
 * Extrato de atendimentos do paciente (?page=patient-financial-print&patientId=): imprime as sessões
 * que estavam FILTRADAS na tela de caixa, no template padrão da entidade. Os dados chegam pela
 * localStorage gravada pela aba Financeiro (sessionStorage não atravessa a aba aberta com noopener); lida e apagada.
 */
export const PATIENT_FINANCIAL_PRINT_KEY = 'clinic4us-patient-financial-print';

export interface FinancialPrintPayload {
  filtersLabel: string;
  rows: FinancialSession[];
}

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const fmtTime = (iso: string | null | undefined) => (iso && iso.length >= 16 ? iso.slice(11, 16) : '');
const timeRange = (r: FinancialSession) => [fmtTime(r.date), fmtTime(r.endDate)].filter(Boolean).join(' - ');
const situationLabel = (s: FinancialSession) => (s.paid ? 'Pago' : 'Pendente');

const PatientFinancialPrint: React.FC = () => {
  const { getParam } = useRouter();
  const { user } = useAuth();
  const patientId = getParam('patientId') || '';
  const [patient, setPatient] = useState<PatientDetail | null>(null);
  const [payload, setPayload] = useState<FinancialPrintPayload>({ filtersLabel: '', rows: [] });
  const [templates, setTemplates] = useState<PrintTemplate[]>([]);
  const templateChoice = usePrintTemplateChoice(templates, 'patient-financial');
  const template = templateChoice.template;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Clinic4Us - Extrato de atendimentos';
    const load = async () => {
      try {
        let stored: FinancialPrintPayload = { filtersLabel: '', rows: [] };
        try {
          const raw = localStorage.getItem(PATIENT_FINANCIAL_PRINT_KEY) ?? sessionStorage.getItem(PATIENT_FINANCIAL_PRINT_KEY);
          if (raw) stored = JSON.parse(raw);
          localStorage.removeItem(PATIENT_FINANCIAL_PRINT_KEY);
        } catch { /* vazio */ }
        const [patientData, templates] = await Promise.all([
          patientsService.getById(patientId),
          user?.entityId ? printTemplateService.getByEntity(user.entityId).catch(() => []) : Promise.resolve([]),
        ]);
        setPatient(patientData);
        setPayload(stored);
        setTemplates(templates);
      } catch {
        setError('Não foi possível carregar os dados para impressão.');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: colors.primary }} /></Box>;
  }
  if (error || !patient) {
    return <Typography sx={{ color: colors.error, textAlign: 'center', py: 8 }}>{error || 'Paciente não encontrado.'}</Typography>;
  }

  const rows = payload.rows;
  const totalTable = rows.reduce((t, r) => t + r.unitValue, 0);
  const totalDiscount = rows.reduce((t, r) => t + r.patientDiscount, 0);
  const totalValue = rows.reduce((t, r) => t + (r.paid ? (r.paidValue ?? r.netValue) : r.netValue), 0);
  const totalPaid = rows.filter(r => r.paid).reduce((t, r) => t + (r.paidValue ?? 0), 0);
  const totalOpen = rows.filter(r => !r.paid).reduce((t, r) => t + r.netValue, 0);
  const cell: React.CSSProperties = { padding: '1.6mm 2mm', borderBottom: '1px solid #ddd', fontSize: '10pt', verticalAlign: 'top' };
  const right: React.CSSProperties = { ...cell, textAlign: 'right', whiteSpace: 'nowrap' };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '2rem', '@media print': { padding: 0, backgroundColor: '#fff' } }}>
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          .fin-print-page { padding: 0 !important; width: auto !important; min-height: 0 !important; box-shadow: none !important; margin: 0 !important; }
          .print-frame-header img { max-height: 18mm !important; }
          thead { display: table-header-group; }
          tr { break-inside: avoid; }
        }
      `}</style>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, maxWidth: '210mm', mx: 'auto', '@media print': { display: 'none' } }}>
        <PrintTemplateBar choice={templateChoice} />
        <Button variant="contained" startIcon={<Print />} onClick={() => window.print()}
          sx={{ backgroundColor: colors.primary, textTransform: 'none', '&:hover': { backgroundColor: '#029AAB' } }}>
          Imprimir
        </Button>
      </Box>

      <Box className="fin-print-page" sx={{
        width: '210mm', maxWidth: '100%', minHeight: '297mm', padding: '15mm', boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
        background: 'white', margin: '0 auto 2rem', boxShadow: '0 0 8px rgba(0,0,0,0.15)', fontSize: '12pt', color: '#000',
      }}>
        {template && (
<div className="print-frame-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10mm' }}>
            <div style={{ flexShrink: 0 }}>
              {template?.file ? <img src={template.file} alt="logo" style={{ maxHeight: '30mm', maxWidth: '70mm', objectFit: 'contain' }} /> : null}
            </div>
            <div style={{ textAlign: 'right', fontSize: '10pt', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template?.header) }} />
          </div>
          <hr style={{ border: 'none', borderTop: '1px solid #999', margin: '6mm 0' }} />
        </div>
)}

        <Box sx={{ flex: '1 1 auto' }}>
          <Typography sx={{ fontSize: '15pt', fontWeight: 700, color: '#212529', mb: '4mm', letterSpacing: '.03em' }}>Extrato de atendimentos</Typography>
          <Box sx={{ mb: '5mm', fontSize: '11pt', color: '#212529', lineHeight: 1.6 }}>
            <div><strong>Paciente:</strong> {patient.patientName}</div>
            {payload.filtersLabel && <div><strong>Recorte:</strong> {payload.filtersLabel}</div>}
            <div><strong>Emitido em:</strong> {new Date().toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
          </Box>

          {rows.length === 0 ? (
            <Typography sx={{ color: '#6c757d', py: 4, textAlign: 'center' }}>Nenhuma sessão no recorte escolhido.</Typography>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f1f3f5' }}>
                  <th style={{ ...cell, textAlign: 'left', fontWeight: 600 }}>Data</th>
                  <th style={{ ...cell, textAlign: 'left', fontWeight: 600 }}>Serviço / Profissional</th>
                  <th style={{ ...right, fontWeight: 600 }}>Tabela</th>
                  <th style={{ ...right, fontWeight: 600 }}>Desconto</th>
                  <th style={{ ...right, fontWeight: 600 }}>Valor</th>
                  <th style={{ ...cell, textAlign: 'center', fontWeight: 600 }}>Situação</th>
                  <th style={{ ...cell, textAlign: 'left', fontWeight: 600 }}>Pagamento</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                      <div>{fmtDate(r.date)}</div>
                      {timeRange(r) && <div style={{ fontSize: '9pt', color: '#6c757d' }}>{timeRange(r)}</div>}
                    </td>
                    <td style={cell}>
                      <div>{r.serviceTypeName || 'Atendimento'}</div>
                      {r.professionalName && <div style={{ fontSize: '9pt', color: '#6c757d' }}>{r.professionalName}</div>}
                    </td>
                    <td style={right}>{brl(r.unitValue)}</td>
                    <td style={right}>{r.paid ? (r.paymentDiscount ? `- ${brl(r.paymentDiscount)}` : '—') : (r.patientDiscount ? `- ${brl(r.patientDiscount)}` : '—')}</td>
                    <td style={{ ...right, fontWeight: 600 }}>{brl(r.paid ? (r.paidValue ?? r.netValue) : r.netValue)}</td>
                    <td style={{ ...cell, textAlign: 'center', whiteSpace: 'nowrap' }}>{situationLabel(r)}</td>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                      {r.paid && r.paymentDate ? fmtDate(r.paymentDate) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2} style={{ ...cell, borderBottom: 'none', fontWeight: 600 }}>{rows.length} sessão(ões)</td>
                  <td style={{ ...right, borderBottom: 'none', color: '#6c757d' }}>{brl(totalTable)}</td>
                  <td style={{ ...right, borderBottom: 'none', color: '#6c757d' }}>{totalDiscount ? `- ${brl(totalDiscount)}` : '—'}</td>
                  <td style={{ ...right, borderBottom: 'none', fontWeight: 700 }}>{brl(totalValue)}</td>
                  <td style={{ ...cell, borderBottom: 'none' }} />
                  <td style={{ ...cell, borderBottom: 'none' }} />
                </tr>
                <tr>
                  <td colSpan={4} style={{ ...right, borderBottom: 'none', color: '#6c757d', paddingTop: 0 }}>Pago</td>
                  <td style={{ ...right, borderBottom: 'none', fontWeight: 700, paddingTop: 0 }}>{brl(totalPaid)}</td>
                  <td colSpan={2} style={{ ...cell, borderBottom: 'none' }} />
                </tr>
                <tr>
                  <td colSpan={4} style={{ ...right, borderBottom: 'none', color: '#6c757d', paddingTop: 0 }}>Em aberto</td>
                  <td style={{ ...right, borderBottom: 'none', fontWeight: 700, paddingTop: 0, color: totalOpen > 0 ? '#dc3545' : '#212529' }}>{brl(totalOpen)}</td>
                  <td colSpan={2} style={{ ...cell, borderBottom: 'none' }} />
                </tr>
              </tfoot>
            </table>
          )}

        </Box>

        {template && (
<div style={{ marginTop: 'auto', textAlign: 'center', fontSize: '9pt', paddingTop: '6mm', borderTop: '1px solid #ddd' }} dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template?.footer) }} />
)}
      </Box>
    </Box>
  );
};

export default PatientFinancialPrint;
